import { useMutation } from '@tanstack/react-query';

import type { Insight } from '../../domain/insight';
import { InsightType } from '../../domain/insightType';
import type { Competitor, Opportunity } from '../../domain/opportunity';
import { queryClient } from '../../queryClient';
import { appStore } from '../../services/appStore';
import { useCompanyQuery } from '../company/useCompanyQuery';
import { insightsQueryKey } from '../insights/insightsQueryKey';
import { fetchOpportunitiesInsights } from '../insights/useInsightsQuery';

import { competitorsApi } from './api';
import { makeCompetitorsQueryKey } from './useCompetitorsQuery';

const updateCompetitors = async (newCompetitorDomains: string[], prospect: string) => {
  await competitorsApi.updateCompetitors(prospect, newCompetitorDomains);
  const prevCompetitors = queryClient.getQueryData(makeCompetitorsQueryKey(prospect)) as Competitor[];
  const notSelectedCompetitors = prevCompetitors
    .filter((competitor) => !newCompetitorDomains.includes(competitor.domain))
    .map((competitor) => ({ ...competitor, isSelected: false }));
  const selectedCompetitors = newCompetitorDomains.map((domain) => ({ domain, isSelected: true }));
  const newCompetitors = selectedCompetitors.concat(notSelectedCompetitors);
  queryClient.setQueryData(makeCompetitorsQueryKey(prospect), newCompetitors);
};

const updateOpportunities = async (opportunity: Opportunity, prospect: string) => {
  const { emailsToGenerate } = appStore.getState().emailGenerationOptions;

  const insights = queryClient.getQueryData(insightsQueryKey.insights(prospect)) as Insight[];
  const updatedOpportunities = await fetchOpportunitiesInsights(prospect);

  const regeneratedOpportunityId = updatedOpportunities.find(
    (o) => o.benchmark.metric === opportunity.benchmark.metric
  )?.id;

  regeneratedOpportunityId && appStore.setRegeneratedOpportunitiesIds([regeneratedOpportunityId]);
  appStore.setUpdatedOpportunities(updatedOpportunities);

  const updatedInsights = insights.map((insight, index) => {
    if (insight.insightType !== InsightType.Opportunity) {
      return insight;
    }

    const updatedOpportunity = updatedOpportunities.find((o) => o.benchmark.code === insight.benchmark.code);

    if (index >= emailsToGenerate || regeneratedOpportunityId === updatedOpportunity?.id) {
      return updatedOpportunity ?? insight;
    }

    return insight;
  });

  queryClient.setQueryData(insightsQueryKey.insights(prospect), updatedInsights);
};

export const useUpdateCompetitorsMutation = (opportunity: Opportunity, onFinish: () => void) => {
  const { domain: prospect = '' } = useCompanyQuery().data ?? {};

  return useMutation({
    mutationKey: ['update-competitors'],
    mutationFn: async (selectedCompetitors: string[]) => {
      await updateCompetitors(selectedCompetitors, prospect);
      await updateOpportunities(opportunity, prospect);
    },
    onSuccess: onFinish,
  });
};
